import React, { useState, useEffect } from 'react';
import HelpDocumentation from './HelpDocumentation';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function ScoreEntry() {
    const [events, setEvents] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState('');
    const [competitions, setCompetitions] = useState([]);
    const [selectedCompetitionID, setSelectedCompetitionID] = useState('');
    const [competitors, setCompetitors] = useState([]);
    const [scoringComponents, setScoringComponents] = useState([]);
    const [scores, setScores] = useState([]);
    const [editableRow, setEditableRow] = useState(null);
    const [originalScores, setOriginalScores] = useState([]);
    const [selectedCompetition, setSelectedCompetition] = useState(null);

    // Add loading state variables
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingCompetitions, setLoadingCompetitions] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [refreshingScores, setRefreshingScores] = useState(false);

    // Add state variables for modal
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editedCompetitorID, setEditedCompetitorID] = useState(null);
    const [editedAttemptNumber, setEditedAttemptNumber] = useState(1);
    const [tempScores, setTempScores] = useState([]);

    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

    const openHelpModal = () => setIsHelpModalOpen(true);
    const closeHelpModal = () => setIsHelpModalOpen(false);

    // Add state to track if registration is required
    const [registrations, setRegistrations] = useState([]);

    useEffect(() => {
        const fetchEvents = async () => {
            setLoadingEvents(true);
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                const enabledEvents = Array.isArray(data) ? data.filter(event => event.Enabled) : [];
                setEvents(enabledEvents);
                if (enabledEvents.length > 0) {
                    setSelectedEventID(enabledEvents[0].EventID); // Auto-select first enabled event
                }
            } catch (error) {
                console.error('Error fetching events:', error);
            } finally {
                setLoadingEvents(false);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        if (!selectedEventID) return;
        setSelectedCompetitionID(''); // Reset competition selection
    }, [selectedEventID]);

    useEffect(() => {
        if (!selectedEventID) return;
        setSelectedCompetitionID(''); // Reset competition selection
        const fetchCompetitions = async () => {
            setLoadingCompetitions(true);
            try {
                const response = await fetch(`${API_BASE_URL}/getCompetitions?EventID=${selectedEventID}`);
                const data = await response.json();
                setCompetitions(Array.isArray(data) ? data.filter(comp => (!comp.TeamBased)) : []);
            } catch (error) {
                console.error('Error fetching competitions:', error);
            } finally {
                setLoadingCompetitions(false);
            }
        };
        fetchCompetitions();
    }, [selectedEventID]);

    useEffect(() => {
        if (selectedCompetitionID) {
            // Get the selected competition details
            const competition = competitions.find(comp => comp.CompetitionID.toString() === selectedCompetitionID.toString());
            setSelectedCompetition(competition);
            
            setLoadingData(true);
            
            // If registration is required, we need to get registered competitors
            if (competition && competition.RegistrationRequired) {
                Promise.all([
                    fetchRegistrations(selectedEventID, selectedCompetitionID),
                    fetchScoringComponents(selectedCompetitionID),
                    fetchScores(selectedEventID, selectedCompetitionID)
                ]).then(([registrationsData]) => {
                    // Only fetch competitors after we have registrations
                    fetchCompetitors(selectedEventID, selectedCompetitionID, competition.RegistrationRequired, registrationsData);
                }).finally(() => {
                    setLoadingData(false);
                });
            } else {
                // Original behavior - no registration filtering
                Promise.all([
                    fetchCompetitors(selectedEventID, selectedCompetitionID, false, []),
                    fetchScoringComponents(selectedCompetitionID),
                    fetchScores(selectedEventID, selectedCompetitionID)
                ]).finally(() => {
                    setLoadingData(false);
                });
            }
        }
    }, [selectedCompetitionID]);

    // Add function to fetch registrations
    const fetchRegistrations = async (eventID, competitionID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getRegistrations?EventID=${eventID}`);
            const data = await response.json();
            // Filter registrations for this competition
            const competitionRegistrations = Array.isArray(data) 
                ? data.filter(reg => reg.CompetitionID.toString() === competitionID.toString())
                : [];
            setRegistrations(competitionRegistrations);
            return competitionRegistrations;
        } catch (error) {
            console.error('Error fetching registrations:', error);
            return [];
        }
    };

    const fetchCompetitors = async (eventID, competitionID, registrationRequired, registrationsData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getAllCompetitors?EventID=${eventID}`);
            const data = await response.json();
            
            if (!Array.isArray(data)) {
                setCompetitors([]);
                return;
            }
            
            // If registration is required, filter competitors by registration
            if (registrationRequired && Array.isArray(registrationsData)) {
                const registeredCompetitorIds = registrationsData.map(reg => reg.CompetitorID);
                const filteredCompetitors = data.filter(comp => 
                    registeredCompetitorIds.includes(comp.CompetitorID)
                );
                setCompetitors(filteredCompetitors);
            } else {
                // No registration required, show all competitors
                setCompetitors(data);
            }
            
            return data;
        } catch (error) {
            console.error('Error fetching competitors:', error);
            setCompetitors([]);
        }
    };

    const fetchScoringComponents = async (competitionID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getScoringComponents?CompetitionID=${competitionID}`);
            const data = await response.json();
            setScoringComponents(Array.isArray(data) ? data : []);
            return data;
        } catch (error) {
            console.error('Error fetching scoring components:', error);
        }
    };

    const fetchScores = async (eventID, competitionID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getScores?EventID=${eventID}&CompetitionID=${competitionID}`);
            const data = await response.json();
            setScores(Array.isArray(data) ? data : []);
            return data;
        } catch (error) {
            console.error('Error fetching scores:', error);
        }
    };

    const handleScoreChange = (competitorID, scoringComponentID, value, componentType, attemptNumber) => {
        setScores(prevScores => {
            const updatedScores = Array.isArray(prevScores) ? [...prevScores] : [];
            const scoreIndex = updatedScores.findIndex(score => 
                score.CompetitorID === competitorID && 
                score.ScoringComponentID === scoringComponentID &&
                score.AttemptNumber === attemptNumber
            );
            
            // For INT type, ensure the value is an integer
            if (componentType === 'INT' && value !== '') {
                value = Math.floor(Number(value));
            }
            // For DECIMAL type, preserve the decimal values but ensure it's a number
            else if (componentType === 'DECIMAL' && value !== '') {
                value = Number(value);
            }
            
            if (scoreIndex > -1) {
                updatedScores[scoreIndex].Multiplier = value;
            } else {
                updatedScores.push({
                    CompetitorID: competitorID,
                    ScoringComponentID: scoringComponentID,
                    Multiplier: value,
                    EventID: selectedEventID,
                    CompetitionID: selectedCompetitionID,
                    AttemptNumber: attemptNumber
                });
            }
            return updatedScores;
        });
    };

    // Add increment/decrement handler
    const handleIncrementDecrement = (competitorID, scoringComponentID, componentType, increment, attemptNumber) => {
        const currentScore = scores.find(score => 
            score.CompetitorID === competitorID && 
            score.ScoringComponentID === scoringComponentID &&
            score.AttemptNumber === attemptNumber
        );
        
        let currentValue = currentScore ? Number(currentScore.Multiplier) || 0 : 0;
        let newValue;
        
        // For DECIMAL type, increment/decrement by 0.25 or another appropriate fraction
        if (componentType === 'DECIMAL') {
            newValue = currentValue + (increment * 0.25); // Increment/decrement by 0.25
        } else {
            newValue = currentValue + increment;
            
            // Ensure integer values for INT type
            if (componentType === 'INT') {
                newValue = Math.floor(newValue);
            }
        }
        
        // Prevent negative values if needed
        if (newValue < 0) newValue = 0;
        
        handleScoreChange(competitorID, scoringComponentID, newValue, componentType, attemptNumber);
    };

    const handleBooleanChange = (competitorID, scoringComponentID, checked, attemptNumber) => {
        handleScoreChange(competitorID, scoringComponentID, checked ? 1 : 0, 'BOOL', attemptNumber);
    };

    const handleDeleteScore = async (competitorID, competitionID, attemptNumber) => {
        const confirmDelete = window.confirm(`!!!Warning!!! Are you sure you want to delete the scores for attempt ${attemptNumber}?`);
        if (!confirmDelete) return;
        try {
            const response = await fetch(`${API_BASE_URL}/deleteScore?CompetitorID=${competitorID}&CompetitionID=${competitionID}&AttemptNumber=${attemptNumber}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                refreshScores(); // refreshed scores from server
                alert('Score deleted successfully.');
            } else {
                alert('Error deleting score.');
            }
        } catch (error) {
            console.error('Error deleting score:', error);
        }
    };

    // Open edit modal for a specific competitor and attempt
    const openEditModal = (competitorID, attemptNumber) => {
        const competitor = competitors.find(c => c.CompetitorID === competitorID);
        if (!competitor) return;
        
        // Create scores for this competitor's specific attempt
        const competitorScores = scoringComponents.map(component => {
            const existingScore = scores.find(score => 
                score.CompetitorID === competitorID && 
                score.ScoringComponentID === component.ScoringComponentID &&
                score.AttemptNumber === attemptNumber
            );
            
            return {
                CompetitorID: competitorID,
                ScoringComponentID: component.ScoringComponentID,
                Multiplier: existingScore ? existingScore.Multiplier : (component.ScoringComponentType === 'BOOL' ? 0 : null),
                EventID: selectedEventID,
                CompetitionID: selectedCompetitionID,
                AttemptNumber: attemptNumber,
                ScoringComponentType: component.ScoringComponentType
            };
        });
        
        setTempScores(competitorScores);
        setEditedCompetitorID(competitorID);
        setEditedAttemptNumber(attemptNumber);
        setIsEditModalOpen(true);
    };

    // Close the edit modal
    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditedCompetitorID(null);
        setEditedAttemptNumber(1);
        setTempScores([]);
    };

    // Get temporary score for a scoring component
    const getScore = (scoringComponentID) => {
        const score = tempScores.find(s => s.ScoringComponentID === scoringComponentID);
        return score ? score.Multiplier : '';
    };

    // Update temporary score
    const handleTempScoreChange = (scoringComponentID, value, componentType) => {
        // Prevent negative values and handle invalid input
        if (value === '' || value === '-' || isNaN(Number(value))) value = 0;
        if (Number(value) < 0) value = 0;
        setTempScores(prevScores => {
            const updatedScores = [...prevScores];
            const scoreIndex = updatedScores.findIndex(score => 
                score.ScoringComponentID === scoringComponentID
            );
            
            // Format value based on component type
            if (componentType === 'INT' && value !== '') {
                value = Math.floor(Number(value));
            } else if (componentType === 'DECIMAL' && value !== '') {
                value = Number(value);
            }
            
            if (scoreIndex > -1) {
                updatedScores[scoreIndex] = {
                    ...updatedScores[scoreIndex],
                    Multiplier: value
                };
            }
            
            return updatedScores;
        });
    };

    // Handle boolean toggle in modal
    const handleTempBooleanChange = (scoringComponentID, checked) => {
        handleTempScoreChange(scoringComponentID, checked ? 1 : 0, 'BOOL');
    };

    // Handle increment/decrement in modal
    const handleTempIncrementDecrement = (scoringComponentID, componentType, increment) => {
        const score = tempScores.find(s => s.ScoringComponentID === scoringComponentID);
        let currentValue = score ? Number(score.Multiplier) || 0 : 0;
        let newValue;
        
        if (componentType === 'DECIMAL') {
            newValue = currentValue + (increment * 0.25);
        } else {
            newValue = currentValue + increment;
            if (componentType === 'INT') {
                newValue = Math.floor(newValue);
            }
        }
        
        if (newValue < 0) newValue = 0;
        
        handleTempScoreChange(scoringComponentID, newValue, componentType);
    };

    // Save scores from modal
    const saveScores = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/createOrUpdateScores`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(tempScores.map(score => ({
                    ...score,
                    Multiplier: score.Multiplier === null ? 0 : score.Multiplier
                })))
            });
            
            if (response.ok) {
                // Update the main scores state with the temporary scores
                setScores(prevScores => {
                    const filteredScores = prevScores.filter(score => 
                        !(score.CompetitorID === editedCompetitorID && score.AttemptNumber === editedAttemptNumber)
                    );
                    return [...filteredScores, ...tempScores];
                });
                closeEditModal();
                // Optional: refresh scores from server
                refreshScores();
            } else {
                alert('Error submitting scores. Changes were not saved.');
            }
        } catch (error) {
            console.error('Error submitting scores:', error);
            alert(`Error: ${error.message}. Changes were not saved.`);
        }
    };

    const refreshScores = () => {
        if (selectedEventID && selectedCompetitionID && selectedCompetition) {
            setRefreshingScores(true);
            
            // If registration is required, we need to get registered competitors
            if (selectedCompetition.RegistrationRequired) {
                Promise.all([
                    fetchRegistrations(selectedEventID, selectedCompetitionID),
                    fetchScoringComponents(selectedCompetitionID),
                    fetchScores(selectedEventID, selectedCompetitionID)
                ]).then(([registrationsData]) => {
                    // Only fetch competitors after we have registrations
                    fetchCompetitors(selectedEventID, selectedCompetitionID, selectedCompetition.RegistrationRequired, registrationsData);
                }).finally(() => {
                    setRefreshingScores(false);
                });
            } else {
                // Original behavior - no registration filtering
                Promise.all([
                    fetchCompetitors(selectedEventID, selectedCompetitionID, false, []),
                    fetchScoringComponents(selectedCompetitionID),
                    fetchScores(selectedEventID, selectedCompetitionID)
                ]).finally(() => {
                    setRefreshingScores(false);
                });
            }
        }
    };

    // Generate rows for all competitors with their attempts
    const generateCompetitorRows = () => {
        if (!competitors || !selectedCompetition) return [];
        
        const rows = [];
        const attemptsAllowed = selectedCompetition.AttemptsAllowed || 1;
        
        competitors.forEach(competitor => {
            // Create rows for each allowed attempt
            for (let attemptNum = 1; attemptNum <= attemptsAllowed; attemptNum++) {
                rows.push({
                    ...competitor,
                    attemptNumber: attemptNum
                });
            }
        });
        
        return rows;
    };

    // Get scores for a specific competitor and attempt
    const getCompetitorAttemptScores = (competitorID, attemptNumber) => {
        return scores.filter(score => 
            score.CompetitorID === competitorID && 
            score.AttemptNumber === attemptNumber
        );
    };

    // Get the score value for display in the table
    const getScoreValue = (competitorID, scoringComponentID, attemptNumber) => {
        const score = scores.find(score => 
            score.CompetitorID === competitorID && 
            score.ScoringComponentID === scoringComponentID &&
            score.AttemptNumber === attemptNumber
        );
        
        if (!score) return '-'; // Return a placeholder if no score exists
        
        const component = scoringComponents.find(c => c.ScoringComponentID === scoringComponentID);
        if (component && component.ScoringComponentType === 'BOOL') {
            return score.Multiplier ? 'Yes' : 'No';
        }
        
        return score.Multiplier || 0;
    };

    const competitorRows = generateCompetitorRows();

    // Loading indicator component
    const LoadingSpinner = () => (
        <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading data...</p>
        </div>
    );

    return (
        <div className="flex flex-column gap-1" style={{ width: '100%' }}>
            <h2>Individual Score Entry</h2>

            <div className="flex gap-1 mb-1">
                <label>
                    <select 
                        className="form-select" 
                        value={selectedEventID} 
                        onChange={(e) => setSelectedEventID(e.target.value)}
                        disabled={loadingEvents}
                    >
                        <option value="">
                            {loadingEvents ? "Loading events..." : "Select Event"}
                        </option>
                        {Array.isArray(events) && events.map(event => (
                            <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                        ))}
                    </select>
                </label>

                <label>
                    <select 
                        className="form-select"
                        value={selectedCompetitionID} 
                        onChange={(e) => setSelectedCompetitionID(e.target.value)} 
                        disabled={!selectedEventID || loadingCompetitions}
                    >
                        <option value="">
                            {loadingCompetitions ? "Loading competitions..." : "Select Competition"}
                        </option>
                        {Array.isArray(competitions) && competitions.map(comp => (
                            <option key={comp.CompetitionID} value={comp.CompetitionID}>{comp.CompetitionName}</option>
                        ))}
                    </select>
                </label>
                
                <button 
                    className="btn btn-secondary" 
                    onClick={refreshScores}
                    disabled={!selectedCompetitionID || refreshingScores || loadingData}
                >
                    {refreshingScores ? "Refreshing..." : "Refresh Scores"}
                </button>
                <button 
                    className="btn btn-info" 
                    onClick={openHelpModal}
                >
                    Help
                </button>
            </div>

            {/* Help Modal */}
            {isHelpModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <HelpDocumentation />
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={closeHelpModal}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {selectedCompetitionID && (
                <div className="data-table-wrapper">
                    {loadingData ? (
                        <LoadingSpinner />
                    ) : competitorRows.length > 0 ? (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Attempt</th>
                                    <th>Org.</th>
                                    <th>Division</th>
                                    {Array.isArray(scoringComponents) && scoringComponents.map(component => (
                                        <th key={component.ScoringComponentID} style={{ width: '100px' }}>
                                            {component.ScoringComponentName} ({component.ScoringComponentName === 'Disqualified' ? '∞' : (component.Points > 0 ? '+' : '') + component.Points})
                                        </th>
                                    ))}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {competitorRows.map((row, index) => (
                                    <tr 
                                        key={`${row.CompetitorID}-${row.attemptNumber}`} 
                                        style={{ 
                                            borderBottom: index % selectedCompetition.AttemptsAllowed === selectedCompetition.AttemptsAllowed - 1 ? '2px solid #000' : '1px solid #ddd',
                                            backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' 
                                        }}
                                    >
                                        <td>{row.CompetitorID}</td>
                                        <td>{row.FirstName} {row.LastName}</td>
                                        <td style={{ fontWeight: 'bold' }}>{row.attemptNumber}</td>
                                        <td>{row.Organization}</td>
                                        <td>{row.Division}</td>
                                        {Array.isArray(scoringComponents) && scoringComponents.map(component => (
                                            <td key={component.ScoringComponentID} style={{ width: '100px' }}>
                                                {getScoreValue(row.CompetitorID, component.ScoringComponentID, row.attemptNumber)}
                                            </td>
                                        ))}
                                        <td>
                                            <button 
                                                className="btn btn-primary" 
                                                onClick={() => openEditModal(row.CompetitorID, row.attemptNumber)}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="btn btn-danger" 
                                                onClick={() => handleDeleteScore(row.CompetitorID, selectedCompetitionID, row.attemptNumber)}
                                                style={{ marginLeft: '0.5rem' }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="empty-state">
                            <p>No competitors found for this competition.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Score Edit Modal */}
            {isEditModalOpen && editedCompetitorID && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>
                            Edit Scores for {competitors.find(c => c.CompetitorID === editedCompetitorID)?.FirstName} {competitors.find(c => c.CompetitorID === editedCompetitorID)?.LastName} - ID {competitors.find(c => c.CompetitorID === editedCompetitorID)?.CompetitorID} - Attempt {editedAttemptNumber}
                        </h3>
                        <div className="score-form">
                            {scoringComponents.map(component => (
                                <div key={component.ScoringComponentID} className="score-input-group">
                                    <label>{component.ScoringComponentName} ({component.ScoringComponentName === 'Disqualified' ? '∞' : (component.Points > 0 ? '+' : '') + component.Points})</label>
                                    {component.ScoringComponentType === 'BOOL' ? (
                                        <div className="checkbox-wrapper">
                                            <input
                                                type="checkbox"
                                                checked={getScore(component.ScoringComponentID) === 1}
                                                onChange={(e) => handleTempBooleanChange(component.ScoringComponentID, e.target.checked)}
                                            />
                                            <span>Yes</span>
                                        </div>
                                    ) : (
                                        <div className="number-input-wrapper">
                                            <button 
                                                type="button"
                                                onClick={() => handleTempIncrementDecrement(component.ScoringComponentID, component.ScoringComponentType, -1)}
                                            >−</button>
                                            {component.ScoringComponentType === 'DECIMAL' ? (
                                                <input
                                                    type="number"
                                                    className="mobile-number-field"
                                                    inputMode="decimal"
                                                    pattern="[0-9]*[.,]?[0-9]*"
                                                    value={getScore(component.ScoringComponentID)}
                                                    onChange={(e) => handleTempScoreChange(component.ScoringComponentID, e.target.value, component.ScoringComponentType)}
                                                    step="0.25"
                                                    min="0"
                                                    autoComplete="off"
                                                />
                                            ) : (
                                                <input
                                                    type="number"
                                                    className="mobile-number-field"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    value={getScore(component.ScoringComponentID)}
                                                    onChange={(e) => handleTempScoreChange(component.ScoringComponentID, e.target.value, component.ScoringComponentType)}
                                                    step="1"
                                                    min="0"
                                                    autoComplete="off"
                                                />
                                            )}
                                            <button 
                                                type="button"
                                                onClick={() => handleTempIncrementDecrement(component.ScoringComponentID, component.ScoringComponentType, 1)}
                                            >+</button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-primary" onClick={saveScores}>Save</button>
                            <button className="btn btn-secondary" onClick={closeEditModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Add a note in the UI if registration is required but no competitors are registered */}
            {selectedCompetitionID && selectedCompetition?.RegistrationRequired && !loadingData && competitors.length === 0 ? (
                <div className="alert alert-warning">
                    This competition requires registration, but no competitors are currently registered. 
                    Use the Competition Registration menu to register competitors for this competition.
                </div>
            ) : null}
        </div>
    );
}